// Player display settings (R124, R256): switches and style picks, all
// presentation-only. Nothing here changes a rule — detection, combat and
// the wake's own-faction gate are server truth and stay untouched whatever
// is off.
//
// **Two stores, and the account owns the blob** (R256). The meta server
// keeps one opaque JSON object per account, so the settings follow the
// player to any browser and any device. This browser keeps a copy in one
// `localStorage` key, and that copy is what a client with no meta server
// behind it runs on: a dev lane, a `?server=` walk-in, a private window, a
// signed-out visitor. The meta server reads no field of the blob; this
// module owns the schema.
//
// The local copy is read and written through `ui/localstore.ts`: private
// mode or a full store just means the switches forget. The parse itself is pure (`parseSettings`), so the
// round-trip and the garbage-in cases are unit-testable without a DOM.
//
// **Every server failure is silent.** A 401, a meta server that does not
// answer, a broken fetch: the client keeps running on the local copy and
// nothing waits on the network. That rule is what makes the direct entry
// mode behave exactly as it did before this file learned to sync.
//
// **A newer local blob wins over an older account copy** (R820). A write
// that has not reached the account yet leaves a sync mark in this browser
// (`SETTINGS_SYNC_KEY`): the account it belongs to, the account's
// `updatedAt` the local copy last matched (`base`), and the wall instant of
// the unsent write (`dirty`). The pull keeps the local copy and pushes it
// when the mark names the same account and the account copy has not changed
// since `base`, or changed before the unsent write. Without the mark, a page
// that leaves inside the push debounce, or a page that never pulled (the
// theme workshop did not), lost its last write to the next page's pull.

import { oneOf } from '../util/oneof';
import { getSettings, putAdvisor, putSettings, type Me } from '../net/api';
import { THEME_CHOICES, type ThemeChoice } from './theme';

/**
 * The look lists, from the generated gen/balance.ts — the same
 * data/identity/labels.toml that carries every other display word (R342).
 * The spawn, torpedo, damage and hold looks are no longer settings: a
 * World theme and a Marks theme carry them (themes.md §3.4, §3.5,
 * R807), and `ui/look.ts` resolves them. The lists stay exported here for
 * the modules that name the types.
 */
export {
  SPAWN_STYLES, TORPEDO_STYLES, DAMAGE_STYLES, CHART_READINGS, HOLD_SYMBOLS,
} from '../gen/balance';
import {
  ANCHOR_STYLES, AURA_STYLES, BANNER_STYLES, BEAM_STYLES, CHART_READINGS, DAMAGE_STYLES,
  DEPOSIT_STYLES, DIGIT_STYLES, DOCK_STYLES, GATHER_STYLES, HOLD_FIRE_STYLES, HOLD_SYMBOLS,
  DETONATION_STYLES, LANGUAGES, NOTICE_STYLES, RELOAD_STYLES,
  HIT_HULL_STYLES, HIT_SHIELD_STYLES, HIT_SINK_STYLES,
  SHOT_ARTILLERY_STYLES, SHOT_MELEE_STYLES, SHOT_SENTINEL_STYLES,
  HULL_STYLES, MAP_MARKER_STYLES, OWN_FLEET_STYLES, OWN_HOLD_STYLES, SHIELD_STYLES,
  SHIP_STYLES, SPAWN_STYLES, STRUCTURE_SYMBOLS,
  SQUAD_FLEET_STYLES, SQUAD_HOLD_STYLES, TERRAIN_STYLES, TORPEDO_STYLES,
  THEME_CATEGORIES, THEME_CODE_ALPHABET, THEME_CODE_LEN, THEME_MARKS_FIELDS,
  type ThemeCategory,
} from '../gen/balance';
export type { ThemeCategory } from '../gen/balance';
import {
  BUZZ_EVENTS, CHART, CHROME_RENDER_SCALE_DEFAULT, CHROME_RENDER_SCALE_STEPS, LAYOUT_CHAT_PANE_PX,
  LAYOUT_KEY_DESKTOP, LAYOUT_KEY_PHONE, SEAVIEW_IDLE, VENDORED_THEMES,
  type BuzzEventId,
} from '../gen/presentation';
import { LOCALE, LOCALES, type Locale } from '../gen/strings';
import type { FeatureFlag } from '../gen/featureflags';
import { pickFrom } from '../locale/pick';
import { SETTINGS_KEY } from './settingskey';
import { legacyAdvisorWord } from './advisor';
import { readStore, removeStore, writeStore } from './localstore';
import { UI_SCALE_DEFAULT, setUiScale, snapUiScale } from './uiscale';
import { isObject, num, str } from '../net/json';
import { clamp01 } from '../render/math';
import { setRenderScale } from '../render/dpr';

/** The one localStorage key the blob lives under. It is declared in
 *  `ui/settingskey.ts`, because `gen/strings.ts` reads the same key when
 *  it picks the language and may not import this module (R855). */
export { SETTINGS_KEY } from './settingskey';

/**
 * The pick that says "the custom theme of the stored theme code"
 * (themes.md §2). Each of the six theme categories may hold it.
 */
export const CUSTOM = 'custom';
export type Custom = typeof CUSTOM;

/** The id of one vendored UI theme (themes.md §4). */
export type VendoredUIId = (typeof VENDORED_THEMES.ui)[number]['id'];

/** The vendored UI theme ids, in the row order of the MODDING tab. */
export const VENDORED_UI_IDS: readonly string[] = VENDORED_THEMES.ui.map((t) => t.id);

/** The UI pick: AUTO, a faction theme, a vendored UI theme, or the custom
 *  theme. */
export type ThemePick = ThemeChoice | VendoredUIId | Custom;

/** One theme code per category. '' is "no code stored". */
export type ThemeCodes = Record<ThemeCategory, string>;

/**
 * A theme code in its stored form, upper case, or '' where the text is not
 * a theme code (themes.md §5). A person types a code in any case.
 */
export function normalizeThemeCode(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const code = raw.trim().toUpperCase();
  if (code.length !== THEME_CODE_LEN) return '';
  for (const ch of code) if (!THEME_CODE_ALPHABET.includes(ch)) return '';
  return code;
}

/** Every category with no code stored. */
export function noCodes(): ThemeCodes {
  return Object.fromEntries(THEME_CATEGORIES.map((c) => [c, ''])) as ThemeCodes;
}

export type SpawnStyle = (typeof SPAWN_STYLES)[number];
export type TorpedoStyle = (typeof TORPEDO_STYLES)[number];
/** How a damage figure appears at a struck hull and goes (R538). */
export type DamageStyle = (typeof DAMAGE_STYLES)[number];
/** Which reading of the sea chart the map draws (R548). */
export type ChartSetting = (typeof CHART_READINGS)[number];
/** The groups of chart pieces of the VIEW panel (R1812), in the order the
 *  panel draws them: `[chart.groups]` of chart.toml. DEPTH and CURRENTS
 *  stand under their flags (R2616), WRECKS in every world (R2712). A
 *  stored id no group holds now (`contacts`, and `cards` of before R2832)
 *  drops on the parse. */
export const CHART_GROUPS = ['names', 'tracks', 'depth', 'currents', 'wrecks'] as const satisfies readonly (keyof typeof CHART.groups)[];
export type ChartGroup = (typeof CHART_GROUPS)[number];

/**
 * The groups the VIEW panel lists for a world (R2616): every group with no
 * flag, and a group with a flag only while `has` says the flag is on, so a
 * world with the flags of v2 off shows the panel of today.
 */
export function shownGroups(has: (flag: FeatureFlag) => boolean): ChartGroup[] {
  return CHART_GROUPS.filter((g) => {
    const flag: string = CHART.groupFlags[g];
    return flag === '' || has(flag as FeatureFlag);
  });
}

/** Which chart convention every hold is drawn as (R633). */
export type HoldSymbol = (typeof HOLD_SYMBOLS)[number];

/**
 * The look lists of the World and the Marks body that no setting holds
 * (themes.md §3.4, §3.5, R822). A theme body carries them, and the module
 * that draws one names the type here, beside the four types above.
 */
export type TerrainStyle = (typeof TERRAIN_STYLES)[number];
export type BeamStyle = (typeof BEAM_STYLES)[number];
export type AuraStyle = (typeof AURA_STYLES)[number];
export type NoticeStyle = (typeof NOTICE_STYLES)[number];
export type DockStyle = (typeof DOCK_STYLES)[number];
export type BannerStyle = (typeof BANNER_STYLES)[number];
export type OwnFleetStyle = (typeof OWN_FLEET_STYLES)[number];
export type SquadFleetStyle = (typeof SQUAD_FLEET_STYLES)[number];
export type ShieldStyle = (typeof SHIELD_STYLES)[number];
export type DigitStyle = (typeof DIGIT_STYLES)[number];
export type OwnHoldStyle = (typeof OWN_HOLD_STYLES)[number];
export type SquadHoldStyle = (typeof SQUAD_HOLD_STYLES)[number];
export type DepositStyle = (typeof DEPOSIT_STYLES)[number];
/** The gun shot looks, one list per shooter role (R1007). */
export type ShotMeleeStyle = (typeof SHOT_MELEE_STYLES)[number];
export type ShotArtilleryStyle = (typeof SHOT_ARTILLERY_STYLES)[number];
export type ShotSentinelStyle = (typeof SHOT_SENTINEL_STYLES)[number];
/** The hit looks: the shields hold, the shields are down, the fleet goes
 *  down (R1007). */
export type HitShieldStyle = (typeof HIT_SHIELD_STYLES)[number];
export type HitHullStyle = (typeof HIT_HULL_STYLES)[number];
export type HitSinkStyle = (typeof HIT_SINK_STYLES)[number];
/** What a Dredger draws while it cuts a deposit (R1120). ONE look serves the
 *  gather and the R715 shaft. */
export type GatherStyle = (typeof GATHER_STYLES)[number];
/** What a hull draws while its anchor goes down, holds and comes up (R1125).
 *  ONE look serves every anchor hull. */
export type AnchorStyle = (typeof ANCHOR_STYLES)[number];
/** What an own fleet draws around its hull while it holds fire (R1439). ONE
 *  look serves every own hull. */
export type HoldFireStyle = (typeof HOLD_FIRE_STYLES)[number];
/** What an own hull with tubes draws for its tube reload, its rounds and
 *  its round (R1474). ONE look serves every own hull with tubes. */
export type ReloadStyle = (typeof RELOAD_STYLES)[number];
/** What a round draws where it goes off, out to the splash of its kind
 *  (R2079). ONE look serves every round. */
export type DetonationStyle = (typeof DETONATION_STYLES)[number];
/** The ship icon set the whole roster wears (R835). */
export type ShipStyle = (typeof SHIP_STYLES)[number];
/** The set all nine strategic objects are drawn as (R1103). ONE field names
 *  the whole set, so there is no field per object kind. */
export type StructureSymbol = (typeof STRUCTURE_SYMBOLS)[number];
/** The look of a map marker (R1291): five looks of mockup round 062. */
export type MapMarkerStyle = (typeof MAP_MARKER_STYLES)[number];
/** What one hull wears: a set of its own, or `follow` for the set (R835). */
export type HullStyle = (typeof HULL_STYLES)[number];
/**
 * The twelve hull fields of a Marks body, `shipCorsair` to `shipDredger`
 * (R835). The plain `ship` field is the set and is not one of them.
 */
export type MarksHullField = Exclude<
  keyof typeof THEME_MARKS_FIELDS & `ship${string}`, 'ship'
>;

export interface GameSettings {
  /** Which banner the DOM chrome wears (R243). `'auto'` follows the
   *  faction the surface knows; 1..5 pins one; a vendored UI theme id
   *  wears that theme's body (themes.md §4); `custom` wears the UI theme
   *  of `codes.ui` (themes.md §3.1). The canvas is not themed: the five
   *  faction colours on the map are the map's legend. */
  theme: ThemePick;
  /** The seaquake's camera shudder (render/quake.ts → camera.setShake). */
  screenshake: boolean;
  /** The decorative layers: creatures, fog, marine snow, haze, wakes and
   *  quake silt. All client-only; none of it is simulated or shared. */
  fluff: boolean;
  /** The sonar presentation: coverage rings, pings, blips, reveal easing.
   *  Detection itself is server-side — this hides nothing from anyone. */
  sonar: boolean;
  /**
   * The desktop chat setting (R1838, ui-map-interface.md §12.0 "The
   * chat"): `small` and `large` float the chat box at the bottom-left of
   * the map as a unit of the console, and `docked` stands the chat pane at
   * the foot of the dock column. The size press, DOCK and UNDOCK of the
   * chat head and the row of SOCIAL write it. The chat band of a
   * telephone reads none of the four chat keys.
   */
  chatDesk: ChatDesk;
  /**
   * The size of the chat box: LARGE when true (R1665, R1838). It keeps the
   * last of SMALL and LARGE while the chat is DOCKED, so UNDOCK floats the
   * box at that size again.
   */
  chatLarge: boolean;
  /**
   * The queue strip of a desktop (R2740, ui-map-interface.md §12 "The
   * queue strip"): `queueOpen` is false while the strip stands minimized
   * to its queue line, and `queueLarge` is the size of the open strip,
   * LARGE (the whole rows with their presses) or SMALL (one line per job).
   * The size stays while the strip is minimized, so the restore glyph
   * opens it at that size again. A telephone reads neither key.
   */
  queueOpen: boolean;
  queueLarge: boolean;
  /**
   * The desktop casino setting (R2198, ui-map-interface.md §12.4):
   * `undocked` opens ECONOMY · CASINO as the casino pane, wide over the
   * map, and `docked` keeps the casino in the dock column. UNDOCK and DOCK
   * of the casino head and the row of INTERFACE write it. A telephone keeps
   * the sheet and reads it not.
   */
  casinoDesk: CasinoDesk;
  /**
   * The chat box of SMALL and LARGE is open, or closed (R218, R1838). A
   * closed box rests as its chat line at the bottom-left (`peek`, R2057).
   * The CHAT tile, the – of the box head and a press on the peek write it.
   */
  chat: boolean;
  /**
   * The chat is on, one flag per form factor (R2369): `chatOn` for the
   * box and the pane of a desktop, `chatOnPhone` for the band of a
   * telephone. Off takes the chat and its chat line away; VIEW keeps the
   * count. The CHAT tile of VIEW writes it, every way that opens the chat
   * turns it on again, and the account keeps it with the rest of the blob.
   * The – of the box head and ▾ of the band head do not write it: they
   * close the chat to its chat line.
   */
  chatOn: boolean;
  chatOnPhone: boolean;
  /**
   * The stop of the DOCKED chat pane (R1799, R1870): the chat line alone,
   * the pane at its own height `chatPanePx`, or the whole dock column. A
   * release of the grip of the pane and the CHAT tile of VIEW write it.
   */
  chatStop: ChatStop;
  /**
   * The height of the DOCKED chat pane at the stop `pane`, in chrome px
   * (R1870): the height the last drag of its grip ended at. The pane
   * stands at most as high as the dock column, so a shorter window shows
   * it lower and keeps the stored height for a taller one.
   */
  chatPanePx: number;
  /**
   * The bottom-left console units, one boolean each: is this unit
   * open, or minimized onto the VIEW button (R218, R1747)?
   *
   * `minimap` used to be two questions — an OPTIONS switch that said "the
   * player wants no chart" and a phone-only ▦ MAP button that said "show me
   * the chart now". One toggle makes them one question with one answer, so
   * there is one flag, and it is the store behind the MINIMAP tile of the
   * VIEW panel in both form factors.
   *
   * `legend` is the legend card of the chart (R1805): open, or minimized
   * to its chip. Which heatmap paints the map is server state and is not
   * kept between sessions; minimizing the legend must not turn the field
   * off.
   *
   * `factionKey` is the switch behind the LEGEND tile, one flag per form
   * factor (R1805, round 083 open point 6, R2832): `factionKey` is the
   * legend window of a desktop (the legend card and the cartouche,
   * `ui/chart/stack.ts`), and `factionKeyPhone` the key section and the
   * DEPTH and CURRENT sections of the VIEW panel on a telephone. The two
   * keep their stored names, so a stored pick keeps its state.
   * `[presentation.layout]` states the two defaults. The World field
   * `factionKey` decides whether a key section exists at all.
   */
  minimap: boolean;
  legend: boolean;
  factionKey: boolean;
  factionKeyPhone: boolean;
  /**
   * Which reading of the sea-chart furniture the map draws (R548, mockup
   * rounds 28, 29 and 31): the compass rose, the place names, the isobaths
   * and the rest. `off` draws no piece at all, `historical` draws the old
   * chart and `modern` draws the survey chart. An arcade match draws no
   * piece whatever this says.
   */
  chart: ChartSetting;
  /** Background music. Off pauses the transport, it does not only mute it. */
  musicOn: boolean;
  /** The sound effects. Off fires no cue at all — the work is skipped. */
  sfxOn: boolean;
  /**
   * Does the game take the sound of the phone over (R695)?
   *
   * Off, the audio session is `ambient`: the game mixes with the app that
   * plays already, and the ring/silent switch mutes the game. On, the
   * session is `playback`: the phone stops the other app, and the game is
   * heard in silent mode. Off is the default, because a podcast that stops
   * on its own is worse than a game that is quiet.
   */
  audioTakeover: boolean;
  /** Music bus gain, 0..1. */
  musicVol: number;
  /** Effects bus gain, 0..1. Effects sit above music by default: they are
   *  the half that carries information. */
  sfxVol: number;
  /** The chosen music playlist id. '' means "the manifest's default", and
   *  so does an id the loaded manifest does not carry. `custom` plays the
   *  Music theme of `codes.music`. */
  playlist: string;
  /** The chosen SFX theme id, on the same '' rule as `playlist`. `custom`
   *  sounds the SFX theme of `codes.sfx`. */
  sfxTheme: string;
  /**
   * The World, Marks and Chart picks (themes.md §3.4-§3.6): the id of a
   * vendored theme, `auto` for today's look, or `custom`. An id this build
   * does not carry parses to `auto`.
   */
  world: string;
  marks: string;
  chartTheme: string;
  /** The theme code of each category's custom theme. '' is none. A code
   *  stays stored while the pick is vendored, so CUSTOM comes back to it. */
  codes: ThemeCodes;
  /**
   * The language every screen is written in (i18n.md §2, R855, R2207):
   * a tag of `LANGUAGES`, English until the player picks another one.
   * The game never reads the language of the browser. The pick itself
   * happens in `gen/strings.ts`, once, when it evaluates — this field is
   * only what the pick reads.
   */
  lang: Locale;
  /**
   * Who a new map marker reaches (R1294): the squad or the faction. The
   * middle of the marker menu flips it, and the menu remembers the last
   * choice here. The first value is the squad, the smaller audience, so a
   * first marker never reaches a whole faction by mistake.
   */
  markerAudience: MarkerAudienceSetting;
  /**
   * The UI scale in percent (R1556, ui-map-interface.md §13 "The UI
   * scale"): one of `UI_SCALE_STEPS`. `ui/uiscale.ts` sets it as CSS
   * `zoom` on the three chrome roots; the map canvas keeps its size.
   */
  uiScale: number;
  /**
   * The BUZZ switches the player turned OFF (R1613, ui-map-interface.md §13
   * "Buzz"): ids of `BUZZ_EVENTS`. Every buzz is on until the player turns
   * it off, so the blob stores the exceptions, and a buzz event a later
   * build adds is on for every player. The hub SETTINGS panel writes it in
   * its ALERTS card; the game reads it when an event arrives.
   */
  buzzOff: BuzzEventId[];
  /**
   * The groups of chart pieces the player hid from the VIEW panel (R1812,
   * round 086 card 15): ids of `CHART_GROUPS`. Every group shows until the
   * player hides it, so the blob stores the exceptions.
   */
  chartHide: ChartGroup[];
  /**
   * The dev stats (ui-map-interface.md §13, R1746): the statistics line
   * `#stats` at the top left of the map, with its AQUAPHOBIA brand line.
   * It is off by default, for every seat; the HELP section of OPTIONS
   * holds the switch.
   */
  devStats: boolean;
  /**
   * The light count of the unread chat lines on the VIEW button and on the
   * CHAT tile, and the CHAT signal of the alarm feed (SOCIAL, R1759). Off
   * takes the figure away and keeps the lines unread; the tabs of the chat
   * box keep their counts.
   */
  chatCountOn: boolean;
  /**
   * The light count of the unread INBOX items on the INBOX cap of a
   * telephone and the INBOX cell of the dock head, and the INBOX row of the
   * alarm list (SOCIAL, R1759). Off takes the figure away and keeps the
   * items unread.
   */
  inboxCountOn: boolean;
  /**
   * The chat time stamp `[HH:MM]` in front of each chat line, in the
   * device's own time zone: the chat of the game and the clan chat
   * card of the hub (SOCIAL, R1791). A replayed line keeps the time it was
   * sent at. Off by default.
   */
  chatTimeOn: boolean;
  /**
   * The chat filter (SOCIAL, R2937, ui-map-interface.md §8 "The chat
   * filter"): it masks strong profanity and slurs in each chat line on
   * this device. `auto` is ON in a store app and OFF in a browser
   * (`chatFilterOn` of `ui/chatfilter.ts`); a press of the switch writes
   * `on` or `off`, and that choice holds on every platform.
   */
  chatFilter: ChatFilterSetting;
  /**
   * The heights of the telephone sheet (ui-map-interface.md §12, R1748).
   * `two`, the default, gives the sheet two heights: closed (the tab bar
   * and the peek line) and open (the full height under the readout line).
   * `three` brings back the grip, the rest stop and a free height.
   */
  sheetHeights: SheetHeightsSetting;
  /**
   * The tab labels of a telephone (ui-map-interface.md §13, R2215).
   * `glyph`, the default, shows the six game tabs as glyphs alone on the
   * tab bar and on the rail, and the INBOX and MENU caps drop their word.
   * `word` (GLYPH + WORD) puts the short word under each glyph where the
   * bar has room. The desktop dock reads it not: it always shows the glyph
   * and the word, measured (R2214).
   */
  tabLabels: TabLabelsSetting;
  /**
   * The mark scale switches (R1981, R1992, ui-map-interface.md §6.1.9):
   * on draws the fleets and the torpedoes of one mode at the figure of
   * `[presentation.mark_scale]`, off at their own size. `markScaleArcade`
   * serves the arcade page, `markScaleCore` the season map. Both are off
   * by default; `arcade/markscale.ts` reads them.
   */
  markScaleArcade: boolean;
  markScaleCore: boolean;
  /**
   * The MIRRORED LAYOUT of a desktop (R2883, ui-map-interface.md §12.0): ON
   * flips the map edge left for right, so the chat box, VIEW, the context
   * card and the round minimap stand next to the dock, and the notices and
   * the legend at the left. Off by default, the layout of R2879. A
   * telephone and the rail read it not; `ui/layout.ts` `setMirrorDesk`
   * takes it.
   */
  mirrorDesk: boolean;
  /**
   * The idle sea view (R2865, ui-map-interface.md §13 "SEA VIEW"): ON lets
   * the map blend into the sea view after `seaIdleMin` minutes with no
   * input. The SEA VIEW section holds the switch and, while it is ON, the
   * bar of the minutes. `[presentation.seaview_idle]` states the two
   * defaults and the range of the bar.
   */
  seaIdle: boolean;
  seaIdleMin: number;
  /**
   * The two look switches of the SEA VIEW section (R2881, seaview.md
   * §3.7). The map draws neither: the sea view page reads both from this
   * blob, in the idle frame and on /seaview/ alike. `seaVeil` ON draws the
   * survey veil over unsurveyed water; `seaLife` ON draws the sea life.
   */
  seaVeil: boolean;
  seaLife: boolean;
  /**
   * The switch POWER SAVER of the SEA VIEW section (R2922, seaview.md
   * §3.7). ON lets the sea view draw a reduced picture at 30 frames a
   * second, to save battery and heat. The sea view page reads it.
   */
  seaPower: boolean;
  /**
   * The render scale in percent of the device pixel ratio (R484 item 2,
   * ui-map-interface.md §10.16, §13 "The render scale"): one of
   * `RENDER_SCALE_STEPS`. The engine surface draws the map at the capped
   * device ratio times the step, so a large screen pays for fewer pixels.
   * The chrome keeps its sharpness; the map alone draws coarser.
   */
  renderScale: number;
}

/** The render scale steps a player can pick, in percent, in the row order:
 *  the map as it is drawn first (R484 item 2). */
export const RENDER_SCALE_STEPS: readonly number[] = CHROME_RENDER_SCALE_STEPS;
/** The step a store with no valid value takes: the map as it is drawn. */
export const RENDER_SCALE_DEFAULT: number = CHROME_RENDER_SCALE_DEFAULT;

/** A stored value as a step: a step passes, and anything else is the
 *  default, as the UI scale does it (R1556). */
export function snapRenderScale(raw: unknown): number {
  return typeof raw === 'number' && RENDER_SCALE_STEPS.includes(raw) ? raw : RENDER_SCALE_DEFAULT;
}

/**
 * Applies a stored render scale to the engine surface of this page. The
 * game page, the arcade page and the replay page call it at boot, before
 * the engine exists; the writes of the blob call it after.
 */
export function applyRenderScale(pct: number): void {
  setRenderScale(snapRenderScale(pct) / 100);
}

/** The two audiences of a map marker, as the setting stores them. */
export const MARKER_AUDIENCE_SETTINGS = ['squad', 'faction'] as const;
export type MarkerAudienceSetting = (typeof MARKER_AUDIENCE_SETTINGS)[number];

/** The three stops of the desktop chat pane (R1799): the chat line, the
 *  pane at its own height (R1870) and the whole dock column. */
export const CHAT_STOPS = ['line', 'pane', 'column'] as const;
export type ChatStop = (typeof CHAT_STOPS)[number];

/** The three values of the desktop chat setting (R1838), the default
 *  first: the chat box at its two sizes, and the chat pane of the dock. */
export const CHAT_DESKS = ['small', 'large', 'docked'] as const;
export type ChatDesk = (typeof CHAT_DESKS)[number];

/** The two values of the desktop casino setting (R2198), the default
 *  first: the casino pane over the map, and the casino in the dock
 *  column. */
export const CASINO_DESKS = ['undocked', 'docked'] as const;

/** The three values of the chat filter (R2937), the default first: the
 *  platform decides, or the player's own choice. */
export const CHAT_FILTERS = ['auto', 'on', 'off'] as const;
export type ChatFilterSetting = (typeof CHAT_FILTERS)[number];
export type CasinoDesk = (typeof CASINO_DESKS)[number];

/** The two choices of the sheet heights (R1748), the default first. */
export const SHEET_HEIGHTS = ['two', 'three'] as const;
export type SheetHeightsSetting = (typeof SHEET_HEIGHTS)[number];

/** The two values of TAB LABELS (R2215), the default first: the glyph
 *  alone, and the glyph over the short word. */
export const TAB_LABELS = ['glyph', 'word'] as const;
export type TabLabelsSetting = (typeof TAB_LABELS)[number];

/** Everything on: the game as designed. A switch is an opt-out. The one
 *  exception is `audioTakeover`, which takes something away from the phone
 *  and so is an opt-in (R695). */
export const DEFAULT_SETTINGS: GameSettings = {
  theme: 'auto',
  screenshake: true,
  fluff: true,
  sonar: true,
  // A desktop floats the chat box at the bottom-left of the map, at its
  // usual size (R1838), and a new device opens it as its peek line, the
  // newest chat line alone, so the first view leaves the map free
  // (R2057). DOCKED rests at the chat line.
  chatDesk: 'small',
  chatLarge: false,
  chat: false,
  chatOn: true,
  chatOnPhone: true,
  chatStop: 'line',
  chatPanePx: LAYOUT_CHAT_PANE_PX,
  // The queue strip of a desktop stands at its line, and opens LARGE, as
  // it did before the small strip (R2740).
  queueOpen: false,
  queueLarge: true,
  // A desktop opens the casino as the wide pane over the map, the owner's
  // ruling of 2026-10-14 (R2198).
  casinoDesk: 'undocked',
  minimap: true,
  legend: true,
  // The faction key: on at a desktop, off at a telephone (R1805).
  factionKey: LAYOUT_KEY_DESKTOP,
  factionKeyPhone: LAYOUT_KEY_PHONE,
  chart: 'modern',
  musicOn: true,
  sfxOn: true,
  // The one switch that is off by default: it takes the sound of another
  // app away, so the player must ask for it (R695).
  audioTakeover: false,
  musicVol: 0.2,
  sfxVol: 0.7,
  playlist: '',
  sfxTheme: '',
  world: 'auto',
  marks: 'auto',
  chartTheme: 'auto',
  codes: noCodes(),
  // The source language (R2207): a player who never opens the row reads
  // the game in English, whatever the browser asks for.
  lang: LOCALES[0],
  // The squad (R1294): a first marker reaches the smaller audience.
  markerAudience: 'squad',
  // 100 %: the chrome as it is drawn (R1556).
  uiScale: UI_SCALE_DEFAULT,
  // Every buzz on (R1613).
  buzzOff: [],
  // Every group of chart pieces shows (R1812).
  chartHide: [],
  // The statistics line is a tool of the developer, so a player sees the
  // map with no figures over it until they ask (R1746).
  devStats: false,
  // Both unread counts show, as they did before the switches (R1759).
  chatCountOn: true,
  inboxCountOn: true,
  // A chat line opens with its sender, as it did before the switch (R1791).
  chatTimeOn: false,
  // The chat filter follows the platform until the player turns it (R2937).
  chatFilter: 'auto',
  // Two heights: a menu opens full and goes away again (R1748).
  sheetHeights: 'two',
  // The glyph alone on a telephone tab (R2215).
  tabLabels: 'glyph',
  // The marks at their own size, in both modes (R1992).
  markScaleArcade: false,
  markScaleCore: false,
  // The plain desktop: the console at the left, the dock at the right (R2883).
  mirrorDesk: false,
  // The idle sea view (R2865): the switch and the minutes of the TOML.
  seaIdle: SEAVIEW_IDLE.defaultOn,
  seaIdleMin: SEAVIEW_IDLE.defaultMin,
  // The look switches of the sea view (R2881): the figures of the TOML.
  seaVeil: SEAVIEW_IDLE.veilOn,
  seaLife: SEAVIEW_IDLE.lifeOn,
  // The switch POWER SAVER (R2922): the figure of the TOML.
  seaPower: SEAVIEW_IDLE.powerOn,
  // The map as it is drawn (R484 item 2): the figure of the TOML.
  renderScale: RENDER_SCALE_DEFAULT,
};

/**
 * The idle time of the bar AFTER (R2865): a whole step of the bar from
 * `minMin` to `maxMin`, or the default for a value that is not a finite
 * number.
 */
export function snapSeaIdleMin(raw: unknown): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return SEAVIEW_IDLE.defaultMin;
  const { minMin, maxMin, stepMin } = SEAVIEW_IDLE;
  const v = Math.min(maxMin, Math.max(minMin, raw));
  return minMin + Math.round((v - minMin) / stepMin) * stepMin;
}

/** The supported tags, as the parse and the LANGUAGE row read them. */
const LANG_TAGS: readonly string[] = LANGUAGES.map((l) => l.tag);

/**
 * The language these settings mean (i18n.md §2). It is the same rule
 * `gen/strings.ts` ran at load, so a comparison against `LOCALE` says
 * whether the page must be reloaded.
 */
export function effectiveLanguage(s: GameSettings): string {
  return pickFrom(LOCALES, s.lang);
}

/** The vendored ids of one category that a stored pick may name. */
const VENDORED_IDS = {
  world: VENDORED_THEMES.world.map((t) => t.id as string),
  marks: VENDORED_THEMES.marks.map((t) => t.id as string),
  chartTheme: VENDORED_THEMES.chart.map((t) => t.id as string),
} as const;

/** The desktop chat setting of a stored blob, a blob of an older build
 *  too (R1838): `chatDesk`, else LARGE for a large box, DOCKED for a pane
 *  over its line stop, else SMALL. */
function chatDeskOf(obj: Record<string, unknown>): ChatDesk {
  const desk = oneOf(CHAT_DESKS, obj.chatDesk);
  if (desk !== undefined) return desk;
  if (obj.chatLarge === true) return 'large';
  if (obj.chatStop === 'pane' || obj.chatStop === 'column') return 'docked';
  return 'small';
}

/**
 * Parses a stored blob back into settings. A field of the wrong type falls
 * back on its own to the default, so a blob written by an older or newer
 * client degrades to "that switch is on" rather than throwing away the
 * fields it does understand. A volume is the one exception: a finite number
 * out of range is clamped, not refused.
 */
export function parseSettings(raw: string | null | undefined): GameSettings {
  const out = {
    ...DEFAULT_SETTINGS, codes: noCodes(), buzzOff: [] as BuzzEventId[], chartHide: [] as ChartGroup[],
  };
  if (!raw) return out;
  try {
    const obj = JSON.parse(raw) as unknown;
    if (!isObject(obj)) return out;
    // Every boolean switch must be named here. A key left off this list
    // parses to its default on every load, so the switch looks like it
    // works and forgets itself at the next reload.
    for (const key of
      ['screenshake', 'fluff', 'sonar', 'minimap', 'legend',
        'factionKey', 'factionKeyPhone', 'musicOn', 'sfxOn', 'audioTakeover', 'devStats',
        'chatCountOn', 'inboxCountOn', 'chatTimeOn', 'chat', 'chatOn', 'chatOnPhone', 'chatLarge',
        'queueOpen', 'queueLarge',
        'markScaleArcade', 'markScaleCore', 'seaIdle', 'seaVeil', 'seaLife', 'seaPower', 'mirrorDesk'] as const) {
      if (typeof obj[key] === 'boolean') out[key] = obj[key];
    }
    // A volume is a gain, so it is clamped rather than refused: a blob from
    // a client that stored percents keeps the loud end of the range instead
    // of silently reverting to the default.
    for (const key of ['musicVol', 'sfxVol'] as const) {
      const v = obj[key];
      if (typeof v === 'number' && isFinite(v)) out[key] = clamp01(v);
    }
    // The idle time is clamped to the bar, as a volume is (R2865).
    if (obj.seaIdleMin !== undefined) out.seaIdleMin = snapSeaIdleMin(obj.seaIdleMin);
    // A playlist or theme id is checked against the manifest at runtime, not
    // here: the store outlives any one published bank, and an id this build
    // has never heard of degrades to the manifest default by itself.
    for (const key of ['playlist', 'sfxTheme'] as const) {
      if (typeof obj[key] === 'string') out[key] = obj[key] as string;
    }
    // A field the build no longer carries is simply ignored: `replayMode`
    // was one until R542, and `spawnStyle`, `torpedoStyle`, `damageStyle`
    // and `holdSymbol` were four until the theme categories took them
    // (R807). A blob that still holds them must parse, and the four picks
    // are dropped: the World and the Marks theme carry those looks now.
    //
    // A chart reading the build does not carry falls back to MODERN, the
    // default (R548).
    out.chart = oneOf(CHART_READINGS, obj.chart) ?? out.chart;
    // A theme the build does not carry falls back to AUTO. A pack will add
    // more, and this client must still parse a blob one of them wrote.
    if (THEME_CHOICES.includes(obj.theme as ThemeChoice) || obj.theme === CUSTOM
      || (typeof obj.theme === 'string' && VENDORED_UI_IDS.includes(obj.theme))) {
      out.theme = obj.theme as ThemePick;
    }
    // The World, Marks and Chart picks follow the same rule: a vendored
    // id this build does not carry is AUTO.
    for (const key of ['world', 'marks', 'chartTheme'] as const) {
      const v = obj[key];
      if (typeof v === 'string' && (v === CUSTOM || VENDORED_IDS[key].includes(v))) out[key] = v;
    }
    // A language this build does not carry is English, and so is the old
    // AUTO and anything that is not a string (R2207). A blob written by a
    // build that speaks more languages therefore reads as English here.
    if (typeof obj.lang === 'string' && LANG_TAGS.includes(obj.lang)) {
      out.lang = obj.lang as Locale;
    }
    // An audience this build does not carry is the squad (R1294).
    out.markerAudience = oneOf(MARKER_AUDIENCE_SETTINGS, obj.markerAudience) ?? out.markerAudience;
    // The desktop chat (R1838). The stop of the DOCKED pane stays as
    // stored (R1799). A blob with no `chatDesk` is older: the floating box
    // of R1665 kept `chat` and `chatLarge`, and the dock of R1799 kept
    // `chatStop`. A large box reads as LARGE, a pane at its middle or
    // whole stop as DOCKED at that stop, and the rest as SMALL.
    out.chatStop = oneOf(CHAT_STOPS, obj.chatStop) ?? out.chatStop;
    // The height of the pane (R1870). A blob of an older build has none:
    // its middle stop `pane` stood at `chat_pane_px`, the default, and the
    // line and the column take no height of their own.
    if (typeof obj.chatPanePx === 'number' && isFinite(obj.chatPanePx) && obj.chatPanePx > 0) {
      out.chatPanePx = Math.round(obj.chatPanePx);
    }
    // The desktop casino (R2198). A value this build does not carry is
    // the default, the pane.
    out.casinoDesk = oneOf(CASINO_DESKS, obj.casinoDesk) ?? out.casinoDesk;
    // A value this build does not carry follows the platform (R2937).
    out.chatFilter = oneOf(CHAT_FILTERS, obj.chatFilter) ?? out.chatFilter;
    out.chatDesk = chatDeskOf(obj);
    if (out.chatDesk !== 'docked') out.chatLarge = out.chatDesk === 'large';
    // A sheet height this build does not carry is the default (R1748).
    out.sheetHeights = oneOf(SHEET_HEIGHTS, obj.sheetHeights) ?? out.sheetHeights;
    // A tab label this build does not carry is the default (R2215).
    out.tabLabels = oneOf(TAB_LABELS, obj.tabLabels) ?? out.tabLabels;
    // A buzz id this build does not carry is dropped, and so is a repeat
    // (R1613): the list names the switches that are off.
    if (Array.isArray(obj.buzzOff)) {
      for (const id of BUZZ_EVENTS) {
        if (obj.buzzOff.includes(id.id)) out.buzzOff.push(id.id);
      }
    }
    // A group this build does not carry is dropped (R1812).
    if (Array.isArray(obj.chartHide)) {
      for (const g of CHART_GROUPS) {
        if (obj.chartHide.includes(g)) out.chartHide.push(g);
      }
    }
    // A UI scale that is not a step is the default (R1556). A blob from a
    // build with other steps therefore reads as 100 % here.
    out.uiScale = snapUiScale(obj.uiScale);
    // A render scale that is not a step is the default (R484 item 2).
    out.renderScale = snapRenderScale(obj.renderScale);
    // A theme code is stored upper case, and a text that is not a code is
    // none. A category this build does not carry is dropped.
    const codes = obj.codes;
    if (isObject(codes)) {
      for (const c of THEME_CATEGORIES) out.codes[c] = normalizeThemeCode(codes[c]);
    }
  } catch { /* garbage in the store: the defaults stand */ }
  return out;
}

/** Reads the settings from localStorage; the defaults where that fails. */
export function loadSettings(): GameSettings {
  return parseSettings(readStore(SETTINGS_KEY));
}

/** Writes the browser's own copy. It never touches the network. A store
 *  that refuses keeps the choice for the session. */
function saveLocal(s: GameSettings): void {
  writeStore(SETTINGS_KEY, JSON.stringify(s));
}

/**
 * Writes the settings: this browser at once, the account soon after.
 *
 * Every caller in the client goes through this one function, so the push is
 * added here and nothing else has to remember it. The local write is
 * synchronous and always happens; the push is debounced and silent, so a
 * client with no meta server behind it behaves exactly as it did before.
 */
export function saveSettings(s: GameSettings): void {
  saveLocal(s);
  // The UI scale applies at the write, on the page that scales its chrome
  // (R1556), and the render scale on the page that draws the map (R484).
  // Every writer comes here, so no row has to remember it.
  setUiScale(s.uiScale);
  applyRenderScale(s.renderScale);
  markDirty();
  schedulePush(s);
  tellWrite(s);
}

/** The listeners of `onSettingsWrite`. */
const writeListeners = new Set<(s: GameSettings) => void>();

/**
 * Calls `fn` with the settings after every write of this page and after
 * the adoption of the account's copy (R1992). Two surfaces of one page
 * that show one key, the VIEW panel and the menu row of the mark scale,
 * stay in step through it. It returns the function that removes `fn`.
 */
export function onSettingsWrite(fn: (s: GameSettings) => void): () => void {
  writeListeners.add(fn);
  return () => { writeListeners.delete(fn); };
}

function tellWrite(s: GameSettings): void {
  for (const fn of [...writeListeners]) fn(s);
}

/**
 * Applies the UI scale and the render scale that another tab of this
 * browser stores (R1576, R484 item 2).
 *
 * The arcade page and the replay page hold no settings surface, so the
 * step comes from the hub SETTINGS panel or the ☸ OPTIONS card. The
 * browser raises `storage` in every other tab of the origin when one of
 * them writes the blob, so a step pressed in the hub reaches an open
 * arcade or replay tab at once. A blob that
 * goes away gives the default, the parser's rule. The page keeps the
 * listener for its whole life; the returned function takes it off, for a
 * test.
 */
export function followStoredUiScale(): () => void {
  if (typeof window === 'undefined') return () => {};
  const follow = (ev: StorageEvent): void => {
    if (ev.key !== SETTINGS_KEY) return;
    const s = parseSettings(ev.newValue);
    setUiScale(s.uiScale);
    applyRenderScale(s.renderScale);
  };
  window.addEventListener('storage', follow);
  return () => window.removeEventListener('storage', follow);
}

// --- the sync mark (R820) ---------------------------------------------------

/** The localStorage key of the sync mark. */
export const SETTINGS_SYNC_KEY = 'aquaphobia.settings.sync';

/** What this browser knows about its copy against the account's. */
export interface SyncMark {
  /** The public id of the account the last pull read, or ''. */
  account: string;
  /** The account copy's `updatedAt` (wall seconds) that the local copy last
   *  matched, through a pull that adopted it or a push that stored it. */
  base: number;
  /** The wall instant (ms) of the newest local write that has not reached
   *  the account, or 0 when every write has. */
  dirty: number;
}

/** Reads the sync mark. A missing or damaged mark is "nothing known". */
export function readSyncMark(): SyncMark {
  try {
    const v = JSON.parse(readStore(SETTINGS_SYNC_KEY) ?? 'null') as unknown;
    if (isObject(v)) return { account: str(v.account), base: num(v.base), dirty: num(v.dirty) };
  } catch { /* a damaged mark: nothing known */ }
  return { account: '', base: 0, dirty: 0 };
}

/** Writes the sync mark. A store that refuses keeps it for the session. */
function writeSyncMark(m: SyncMark): void {
  writeStore(SETTINGS_SYNC_KEY, JSON.stringify(m));
}

/** Records a local write that the account does not hold yet. The instant
 *  only grows, so a write in the same millisecond still counts as newer. */
function markDirty(): void {
  const m = readSyncMark();
  writeSyncMark({ ...m, dirty: Math.max(Date.now(), m.dirty + 1) });
}

/**
 * Does the local copy win over the account copy the pull read? Only for the
 * account the mark names, and only with an unsent write. The account copy
 * then loses when it did not change since `base` — this browser wrote it,
 * so no clock is compared — or when another device wrote it before the
 * unsent write. The second test reads two clocks, so a skewed device clock
 * can move that one case either way.
 */
function localCopyWins(m: SyncMark, account: string, updatedAt: number): boolean {
  if (account === '' || m.account !== account || m.dirty === 0) return false;
  return updatedAt <= m.base || m.dirty / 1000 >= updatedAt;
}

// --- the account's copy (R256) ---------------------------------------------

/**
 * How long a write waits before it reaches the account.
 *
 * A volume drag writes on every step of the range, and each PUT carries the
 * whole blob, so one request per step is a request per 5% of a drag. The
 * window is longer than a drag's own steps and shorter than the time a
 * player takes to close the card, so a settled choice is stored before the
 * tab can be closed on it.
 */
export const SETTINGS_PUSH_MS = 900;

/**
 * Is a session known to exist behind this origin?
 *
 * It starts false and only `pullSettings` turns it on, so the direct entry
 * mode — a dev lane, a `?server=` walk-in — never sends a request at all.
 * A pull that failed leaves it false, which is what stops a signed-out or
 * offline client from firing a PUT at every switch it will never store.
 */
let syncOn = false;

/** The pull runs once per page load. `main.ts` may call it from more than
 *  one place in the boot path, and only the first call does work. */
let pulled = false;

/** The newest blob that has not reached the account yet. */
let pending: GameSettings | null = null;

/** The debounce timer, or 0 while none runs. */
let pushTimer: ReturnType<typeof setTimeout> | 0 = 0;

/**
 * Reads the account's blob and adopts it, once per page load.
 *
 * It resolves to the settings the client must run on, so a caller can paint
 * with the answer. It never rejects and never blocks for long: every
 * failure resolves to the local copy.
 *
 * An account that has stored nothing answers `updatedAt: 0`. That is not an
 * empty settings object to adopt — it is "the account holds no opinion" —
 * so this browser's copy stands, and the next write pushes it up. Adopting
 * the empty answer would erase the settings of a player who had used this
 * browser before the account learned to keep them.
 *
 * `account` is the public id of the signed-in account, where the caller
 * knows it. A local write that the account does not hold yet wins over an
 * older account copy (`localCopyWins`, R820), and the pull pushes it at
 * once. A pull with no account never lets the local copy win, so a write a
 * visitor made before signing in never replaces the account's settings.
 */
export async function pullSettings(account = ''): Promise<GameSettings> {
  if (pulled) return loadSettings();
  pulled = true;
  // The advisor word of an old blob (R1305), read before the parse drops
  // it: the account's copy first, then this browser's (R1566).
  legacyAdvisor = legacyAdvisorWord(readLocalBlob());
  try {
    const stored = await getSettings();
    legacyAdvisor = legacyAdvisorWord(stored.settings) ?? legacyAdvisor;
    // The session is real, so a later write may be pushed.
    syncOn = true;
    const mark = readSyncMark();
    if (localCopyWins(mark, account, stored.updatedAt)) {
      const s = loadSettings();
      pending = s;
      void flushSettings();
      return s;
    }
    if (!stored.updatedAt || !isObject(stored.settings)) {
      writeSyncMark({ account, base: 0, dirty: mark.dirty });
      return loadSettings();
    }
    writeSyncMark({ account, base: stored.updatedAt, dirty: 0 });
    return adoptSettings(stored.settings);
  } catch {
    // No meta server, no session, no network: the local copy is the answer.
    return loadSettings();
  }
}

/**
 * Turns one stored object into settings and caches it in this browser.
 *
 * The object goes through `parseSettings`, exactly as a local blob does, so
 * a field of the wrong type falls back per field and a field this build
 * does not know is dropped. The server owns nothing about the schema, so
 * there is no second parser to keep in step.
 */
export function adoptSettings(body: unknown): GameSettings {
  let next: GameSettings;
  try {
    next = parseSettings(JSON.stringify(body));
  } catch {
    return loadSettings();
  }
  saveLocal(next);
  setUiScale(next.uiScale);
  applyRenderScale(next.renderScale);
  tellWrite(next);
  reloadForLanguage(next);
  return next;
}

/**
 * The mark that says a reload for the language has already happened.
 *
 * The account's copy is adopted after `gen/strings.ts` has picked, so a
 * copy that names another language can only be obeyed by reloading. The
 * mark is what stops that becoming a loop: a page that reloads and still
 * disagrees — a build that no longer speaks the stored language, a store
 * that refuses a write — reloads once and then stands.
 */
export const LANG_RELOAD_KEY = 'aquaphobia.lang.reloaded';

/** Reloads the page once when the adopted settings name another
 *  language than the one this page runs in (R855). */
function reloadForLanguage(s: GameSettings): void {
  const want = effectiveLanguage(s);
  if (want === LOCALE) {
    removeStore(LANG_RELOAD_KEY, 'session');
    return;
  }
  // A session store that refuses gives no mark: the page reloads at most
  // on this one adoption, because the pull runs once per page load.
  if (readStore(LANG_RELOAD_KEY, 'session') === want) return;
  writeStore(LANG_RELOAD_KEY, want, 'session');
  try {
    location.reload();
  } catch { /* no document: a test, a worker */ }
}

/**
 * Queues one blob for the account.
 *
 * One timer runs at a time and the newest blob wins, so twenty steps of a
 * volume drag are one PUT. The timer is only armed where a pull has proved
 * that a session exists.
 */
function schedulePush(s: GameSettings): void {
  if (!syncOn) return;
  pending = { ...s };
  if (pushTimer) return;
  pushTimer = setTimeout(() => { pushTimer = 0; void flushSettings(); }, SETTINGS_PUSH_MS);
}

/**
 * Sends the queued blob now, and answers when the request has settled.
 *
 * It resolves on a failure as well: a settings write must never surface an
 * error to a player. A page that must not leave before its write is stored
 * awaits this (the theme workshop's save does). A stored write moves the
 * sync mark's `base` to the account's new `updatedAt`, and it clears
 * `dirty` unless another write came while the request was out.
 */
export async function flushSettings(): Promise<void> {
  if (pushTimer) { clearTimeout(pushTimer); pushTimer = 0; }
  const s = pending;
  pending = null;
  if (!s || !syncOn) return;
  const sent = readSyncMark().dirty;
  try {
    const out = await putSettings(s as unknown as Record<string, unknown>);
    const m = readSyncMark();
    writeSyncMark({
      ...m,
      base: out.updatedAt || m.base,
      dirty: m.dirty === sent ? 0 : m.dirty,
    });
  } catch { /* the local copy and its mark stand; the next write or pull tries again */ }
}

/** Forgets the sync state. Tests only. */
export function resetSettingsSync(): void {
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = 0;
  pending = null;
  syncOn = false;
  pulled = false;
  legacyAdvisor = null;
}

// --- the advisor switch of the account (R1566) ------------------------------

/** The advisor word an old blob held, as the pull found it, or null. */
let legacyAdvisor: 'on' | 'off' | null = null;

/** The raw object of this browser's blob, or null. */
function readLocalBlob(): unknown {
  const raw = readStore(SETTINGS_KEY);
  try {
    return raw ? JSON.parse(raw) as unknown : null;
  } catch {
    return null;
  }
}

/**
 * Writes the advisor switch to the account (R1566), where a pull proved a
 * session. The world the player sits in hears the EmpireSetting at once;
 * this write is what the next season starts from. A failure is silent,
 * like every settings write.
 */
export function storeAdvisor(on: boolean): void {
  // A press is the player's own setting on this device (R2202).
  writeStore(ADVISOR_SET_KEY, '1');
  if (!syncOn) return;
  void putAdvisor(on).catch(() => { /* the world holds the switch; the next press tries again */ });
}

/** The local storage key: the player pressed the advisor switch in this
 *  browser once (R2202). Only `storeAdvisor` writes it. */
export const ADVISOR_SET_KEY = 'ap.advisor.set';

/** Did the player press the advisor switch in this browser (R2202)? */
export function advisorSetHere(): boolean {
  return readStore(ADVISOR_SET_KEY) === '1';
}

/**
 * Does the client turn the advisor on at the first Empire frame (R2202)?
 * The advisor speaks in the first session of a device: the switch goes on
 * when the empire states it off and the player never pressed it on this
 * device. A meta session is the account's: its join ticket states the
 * switch from the new-commander gate and the player's own word (R1566),
 * so the client sends nothing there. From the second session on, the
 * switch stays as the player or the gate left it.
 */
export function firstSessionAdvisor(o: {
  first: boolean; meta: boolean; on: boolean; set: boolean;
}): boolean {
  return o.first && !o.meta && !o.on && !o.set;
}

/**
 * The one-time copy of an old blob's advisor word to the account (R1566).
 * The switch lived in the blob until R1566. An `on` or an `off` the pull
 * found goes to the account while the account still says `auto`, so a
 * choice the player made in an older client survives; the parse drops
 * the key, so no later pull finds it again. The copy runs before the
 * first join, because the join writes the gate's one-time `off` over
 * `auto`. It updates `me` with the account's answer.
 */
export async function copyLegacyAdvisor(me: Me): Promise<void> {
  const word = legacyAdvisor;
  legacyAdvisor = null;
  if (!word || me.advisor !== 'auto') return;
  try {
    const out = await putAdvisor(word === 'on');
    me.advisor = out.advisor;
    me.advisorOn = out.advisorOn;
  } catch { /* the account keeps auto, and the gate decides */ }
}
